import { eventGroup } from '@ngrx/signals/events';
import { type } from '@ngrx/signals';
import { AuthUser } from '../../models/user.model';
// `import type`, not a value import: `with-auth.reducer.ts` imports `authEvents` from THIS file, so a
// value specifier makes the only import cycle in the workspace. The type is erased at compile time;
// saying so explicitly is what keeps the cycle from coming back (STD-TS-value-import-of-type).
import type { TerritoryAccess } from './with-auth.reducer';

export const authEvents = eventGroup({
  source: 'Auth',
  events: {
    /** Initiate Cognito USER_PASSWORD_AUTH — same for all portals (single pool). */
    login: type<{ username: string; password: string }>(),
    /**
     * Dev-only: bypass Cognito and log in as a preset mock user.
     * Payload is the role string (e.g. 'TERRITORY_ADMIN'). Only handled
     * when environment.mockAuth === true — ignored in production builds.
     */
    mockLogin: type<string>(),
    logout: type<void>(),
    logoutSuccess: type<void>(),
    getUserSuccess: type<AuthUser>(),
    loginSuccess: type<void>(),
    loginFailed: type<string>(),
    setLoading: type<boolean>(),
    clearError: type<void>(),

    /** Cognito returned SOFTWARE_TOKEN_MFA challenge. */
    mfaChallengeRequired: type<{ session: string; username: string }>(),
    /** User submits TOTP code to complete MFA challenge. */
    respondToMfaChallenge: type<{ code: string; session: string; username: string }>(),
    mfaStatusLoaded: type<{ enabled: boolean }>(),

    /** Cognito returned NEW_PASSWORD_REQUIRED (force-change on first sign-in). */
    newPasswordRequired: type<{ session: string; username: string }>(),
    /** User submits a new permanent password to complete the NEW_PASSWORD_REQUIRED challenge. */
    completeNewPassword: type<{ username: string; session: string; newPassword: string }>(),

    /** Begin TOTP enrollment (internal staff whose token lacks an MFA factor). */
    startMfaSetup: type<void>(),
    /** Cognito returned the base32 secret to show as a QR / manual key. */
    mfaSecretLoaded: type<string>(),
    /** User submits their first authenticator code to verify + set TOTP as preferred. */
    submitMfaSetup: type<string>(),

    forgotPassword: type<string>(),
    confirmPasswordReset: type<{ email: string; code: string; newPassword: string }>(),
    /**
     * Cognito accepted the reset code and the new password. Mirrors `changePasswordSuccess`:
     * the reset page flips to its confirmation screen on THIS, never optimistically on dispatch,
     * so a rejected reset leaves the form up with the store error instead of claiming success.
     * The failure arm stays `loginFailed` (it is what populates `AuthStore.error`).
     */
    passwordResetSucceeded: type<void>(),

    /** Signed-in user changes their own password via the Cognito SDK (no REST `/auth/*`). */
    changePassword: type<{ currentPassword: string; newPassword: string }>(),
    changePasswordSuccess: type<void>(),
    changePasswordFailed: type<string>(),

    /** Loaded the list of territories this user can access (post-auth API call). */
    territoriesLoaded: type<TerritoryAccess[]>(),
    /** For National Account Managers: loaded the cross-territory account IDs they manage. */
    managedAccountIdsLoaded: type<string[]>(),

    /** User switches active territory context from the TopBar. */
    setTerritoryContext: type<string | null>(),

    /** Attempt to restore auth state from stored tokens on app init. */
    restoreSession: type<void>(),
    restoreSessionSuccess: type<AuthUser>(),
    restoreSessionFailed: type<void>(),
  },
});
